import React, { useState, useMemo } from 'react'
import { Button, Modal } from 'react-bootstrap'
import {
  PolicyRulesJson,
  EmployeeData,
  ExistingMember,
  EnrollmentMemberPayload,
  PremiumType,
  InfoSection,
} from '../types'
import { useEnrollmentState } from '../hooks/useEnrollmentState'
import { usePremiumCalculation } from '../hooks/usePremiumCalculation'
import { useAuth } from '../../../../auth'
import { KTSVG } from '../../../../../../_metronic/helpers'
import PlanSelector from './PlanSelector'
import SumInsuredSelector from './SumInsuredSelector'
import FamilyMemberForm from './FamilyMemberForm'
import TopUpSelector from './TopUpSelector'
import FlexiSelector from './FlexiSelector'
import PremiumSummary from './PremiumSummary'
import ReadOnlyView from './ReadOnlyView'

interface Props {
  policyRulesJson: PolicyRulesJson
  employeeData: EmployeeData
  existingMembers: ExistingMember[] | null
  policyCrmDbName?: string
  onSuccess: () => void
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')


const UnifiedEnrollmentForm: React.FC<Props> = ({
  policyRulesJson,
  employeeData,
  existingMembers,
  policyCrmDbName,
  onSuccess,
}) => {
  const { currentUser } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const state = useEnrollmentState(policyRulesJson, employeeData, existingMembers)
  const breakdown = usePremiumCalculation({
    json: policyRulesJson,
    employee: employeeData,
    selectedPlanCode: state.selectedPlanCode,
    selectedSICode: state.selectedSICode,
    members: state.members,
    selectedTopUps: state.selectedTopUps,
    selectedFlexiCodes: state.selectedFlexiCodes,
  })

  // ── Derived data ─────────────────────────────────────────
  const selectedPlan = state.eligiblePlans.find((p) => p.planCode === state.selectedPlanCode) ?? null

  const premiumBasisEntry = useMemo(() => {
    if (!state.selectedSICode) return null
    return (
      policyRulesJson.employee.premiumBasis.perSumInsured.find(
        (e) => e.sumInsuredOptionCode === state.selectedSICode
      ) ?? null
    )
  }, [policyRulesJson, state.selectedSICode])

  const premiumType: PremiumType | null = premiumBasisEntry?.premiumType ?? null

  const hasTopUp = policyRulesJson.topUps?.enabled && state.eligibleTopUps.length > 0
  const hasFlexi = policyRulesJson.flexi?.enabled && state.eligibleFlexiItems.length > 0

  // ── Resolve topup/flexi names for payload ────────────────
  const resolveTopUpName = (): string => {
    if (state.selectedTopUps.length === 0) return ''
    return state.selectedTopUps
      .map((s) => {
        const item = policyRulesJson.topUps.items.find((i) => i.itemCode === s.itemCode)
        return item?.displayName || s.itemCode
      })
      .join(', ')
  }

  const resolveTopUpSI = (): number => {
    if (state.selectedTopUps.length === 0) return 0
    let total = 0
    for (const s of state.selectedTopUps) {
      const item = policyRulesJson.topUps.items.find((i) => i.itemCode === s.itemCode)
      const tier = item?.sumInsuredOptions.find((o) => o.optionCode === s.selectedTierCode)
      if (tier?.fixedAmount) total += tier.fixedAmount
    }
    return total
  }

  const resolveFlexiName = (): string => {
    if (state.selectedFlexiCodes.length === 0) return ''
    return state.selectedFlexiCodes
      .map((code) => {
        const item = policyRulesJson.flexi.items.find((i) => i.itemCode === code)
        return item?.displayName || code
      })
      .join(', ')
  }

  // ── Validation ───────────────────────────────────────────
  const validationErrors = useMemo(() => {
    const errors: string[] = []
    if (!state.selectedPlanCode) errors.push('Please select a plan')
    if (!state.selectedSICode) errors.push('Please select sum insured')
    for (const member of state.members) {
      if (!(member.name ?? '').trim()) errors.push(`${member.relation}: Name is required`)
      if (!member.dob) errors.push(`${member.relation}: Date of birth is required`)
      if (!member.gender) errors.push(`${member.relation}: Gender is required`)
    }
    return errors
  }, [state.selectedPlanCode, state.selectedSICode, state.members])

  const canSubmit = validationErrors.length === 0 && !isSubmitting

  // ── Success State ──────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <KTSVG
              path='/media/icons/duotune/general/gen043.svg'
              className='svg-icon-3x svg-icon-success'
            />
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>

          {breakdown.totalPremiumPayable > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of{' '}
                <strong>{formatCurrency(breakdown.totalPremiumPayable)}</strong> will be deducted
                from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ── Read-only mode ───────────────────────────────────────
  if (employeeData.freezeEnrolment) {
    return <ReadOnlyView members={existingMembers || []} policyRulesJson={policyRulesJson} />
  }

  // ── Build Payload ────────────────────────────────────────
  const buildPayload = (): EnrollmentMemberPayload[] => {
    const payload: EnrollmentMemberPayload[] = []

    for (const member of state.members) {
      const base: EnrollmentMemberPayload = {
        EmployeeNumber: currentUser?.EmployeeId || employeeData.EmployeeNumber,
        name: member.name,
        dob: member.dob,
        relation: member.relation,
        gender: member.gender,
        premium: member.premiumAmount,
        Grade: employeeData.Grade,
        crm_client_id: policyCrmDbName || employeeData.CorporateCode,
        Email: employeeData.Email,
        CorporateCode: employeeData.CorporateCode,
        SumInsured: state.computedSI,
      }

      if (member.relation === 'Self') {
        // Add self-only fields
        base.premiumOpted = state.selectedTopUps.length > 0 || state.selectedFlexiCodes.length > 0
        base.additionalPremium = breakdown.totalPremiumPayable
        base.livesCharged = state.members.length
        base.grade = employeeData.Grade

        // TopUp fields
        base.TopupName = resolveTopUpName()
        base.topupsumInsured = resolveTopUpSI()
        base.topup_base_premium = breakdown.topupPremium
        base.topup_gst = breakdown.topupGst
        base.topup_total_premium = breakdown.topupTotal

        // Flexi fields
        base.FlexiName = resolveFlexiName()
        base.flexi_base_premium = breakdown.flexiPremium
        base.flexi_gst = breakdown.flexiGst
        base.flexi_total_premium = breakdown.flexiTotal

        // Base / Total
        base.base_premium = breakdown.basePremium
        base.base_gst = breakdown.baseGst
        base.base_total_premium = breakdown.baseTotal
        base.total_premium_payable = breakdown.totalPremiumPayable
        base.total_gst = breakdown.totalGst
      }

      payload.push(base)
    }

    return payload
  }

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = async () => {
    setShowConfirmModal(false)
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const payload = buildPayload()
      const webhookUrl = 'https://etl.futurisk.in/webhook/7f72b4da-8b83-4927-88d5-03dcdff3b784'

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Submission failed (${response.status})`)
      }

      setShowSuccess(true)
    } catch (err: any) {
      setSubmitError(err.message || 'An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Info section helper ───────────────────────────────────
  const renderInfoSection = (section?: InfoSection) => {
    if (!section?.enabled || !section.contentHtml) return null
    return (
      <div className='alert alert-light-info border border-info border-dashed p-4 mb-5'>
        {section.title && (
          <div className='fw-bolder fs-6 mb-2'>
            <i className='bi bi-info-circle-fill text-info me-2'></i>
            {section.title}
          </div>
        )}
        <div
          className='fs-7'
          dangerouslySetInnerHTML={{ __html: section.contentHtml }}
        />
      </div>
    )
  }

  const hasPremium = breakdown.totalPremiumPayable > 0 || breakdown.basePremium > 0

  // ── Render ───────────────────────────────────────────────
  return (
    <div>
      <div className='row'>
        {/* Main Form Column */}
        <div className='col-lg-8'>
          {/* Sum Insured Selection */}
          <div className='rounded p-4 mb-3' style={{ backgroundColor: '#f9fafb' }}>
            <SumInsuredSelector
              options={state.eligibleSIOptions}
              selectedSICode={state.selectedSICode}
              computedSI={state.computedSI}
              onSelect={state.selectSI}
              showOnlyValue={!hasPremium}
            />
          </div>

          {/* Family Members with plan info inline */}
          {state.selectedSICode && (
            <div className='rounded p-4 mb-3' style={{ backgroundColor: '#f4f6f8' }}>
              <FamilyMemberForm
                members={state.members}
                selectedPlan={selectedPlan}
                premiumType={premiumType}
                availableRelationsToAdd={state.availableRelationsToAdd}
                maxLivesReached={state.maxLivesReached}
                isParentBlocked={state.isParentBlocked}
                isParentInLawBlocked={state.isParentInLawBlocked}
                onAddMember={state.addMember}
                onUpdateMember={state.updateMember}
                onRemoveMember={state.removeMember}
                planSelector={
                  state.eligiblePlans.length > 1 ? (
                    <PlanSelector
                      plans={state.eligiblePlans}
                      selectedPlanCode={state.selectedPlanCode}
                      onSelect={state.selectPlan}
                      compact
                    />
                  ) : null
                }
                planName={selectedPlan?.planName ?? null}
              />
            </div>
          )}

          {/* Top-Up Selection */}
          {hasTopUp && state.selectedPlanCode && (
            <div className='rounded p-4 mb-3' style={{ backgroundColor: '#f9fafb' }}>
              {renderInfoSection(policyRulesJson.topUpInfoSection)}
              <TopUpSelector
                items={state.eligibleTopUps}
                allowMultiple={policyRulesJson.topUps.allowMultiple}
                selectedTopUps={state.selectedTopUps}
                selectedPlanCode={state.selectedPlanCode}
                onSelect={state.setTopUpSelection}
              />
            </div>
          )}

          {/* Flexi Selection */}
          {hasFlexi && state.selectedPlanCode && (
            <div className='rounded p-4 mb-3' style={{ backgroundColor: '#f4f6f8' }}>
              {renderInfoSection(policyRulesJson.flexiInfoSection)}
              <FlexiSelector
                items={state.eligibleFlexiItems}
                selectionMode={policyRulesJson.flexi.selectionMode}
                selectedCodes={state.selectedFlexiCodes}
                selectedPlanCode={state.selectedPlanCode}
                onToggle={state.toggleFlexiItem}
                onSelectSingle={state.setFlexiSingle}
              />
            </div>
          )}

        </div>

        {/* Sidebar — always visible */}
        <div className='col-lg-4'>
          <div className='sticky-top mt-3'>
            {/* Sum Insured + Premium info card */}
            <div className='card card-bordered mb-4'>
              <div className='card-body py-4'>
                <div className='d-flex justify-content-between mb-2'>
                  <span className='text-muted fs-7'>Sum Insured</span>
                  <span className='fw-bolder fs-6 text-primary'>{formatCurrency(state.computedSI)}</span>
                </div>
                {hasPremium && (
                  <PremiumSummary
                    breakdown={breakdown}
                    hasTopUp={!!hasTopUp}
                    hasFlexi={!!hasFlexi}
                  />
                )}
                {!hasPremium && (
                  <div className='d-flex justify-content-between'>
                    <span className='text-muted fs-7'>Premium</span>
                    <span className='fw-bolder fs-6'>₹0</span>
                  </div>
                )}
              </div>
            </div>

            {/* Validation errors */}
            {validationErrors.length > 0 && (
              <div className='alert alert-warning fs-8 py-3'>
                <ul className='mb-0 ps-3'>
                  {validationErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Submit error */}
            {submitError && (
              <div className='alert alert-danger fs-8 py-3'>{submitError}</div>
            )}

            {/* Disclaimer */}
            {renderInfoSection(policyRulesJson.submitInfoSection)}

            {/* Submit Button */}
            <Button
              variant='primary'
              size='lg'
              className='w-100'
              disabled={!canSubmit}
              onClick={() => setShowConfirmModal(true)}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2'></span>
                  Submitting...
                </>
              ) : (
                <>Submit Enrollment</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fs-6'>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please review your enrollment details:</p>
          <div className='card card-bordered'>
            <div className='card-body py-4 fs-7'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted'>Members</span>
                <span className='fw-bold'>{state.members.length}</span>
              </div>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted'>Sum Insured</span>
                <span className='fw-bold'>{formatCurrency(state.computedSI)}</span>
              </div>
              {hasPremium && (
                <PremiumSummary
                  breakdown={breakdown}
                  hasTopUp={!!hasTopUp}
                  hasFlexi={!!hasFlexi}
                />
              )}
              {!hasPremium && (
                <>
                  <div className='separator my-3'></div>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-bolder'>Total Payable</span>
                    <span className='fw-bolder text-primary'>{formatCurrency(0)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className='alert alert-warning d-flex align-items-center mt-4 mb-0 py-3 fs-7'>
            <i className='bi bi-exclamation-triangle-fill text-warning me-2'></i>
            Once confirmed, your enrollment cannot be edited.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmit}>
            Confirm &amp; Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default UnifiedEnrollmentForm
